import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    // Prevent double-initialization
    if (this.dataset.mediaGalleryInitialized === 'true') return;
    this.dataset.mediaGalleryInitialized = 'true';

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // ✅ Clean duplicates on load + keep watching for re-renders
    queueMicrotask(() => this.#dedupeThumbnails());
    this.#startThumbObserver();
  }

  #controller = new AbortController();
  #thumbObserver = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#thumbObserver?.disconnect();
    this.#controller.abort();
  }

  #handleVariantUpdate = (event) => {
    // Guard: avoid multiple swaps
    if (this.dataset.mediaGallerySwapped === 'true') return;

    const source = event?.detail?.data?.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    this.dataset.mediaGallerySwapped = 'true';

    // Allow new gallery to initialize and dedupe
    newMediaGallery.dataset.mediaGalleryInitialized = 'false';
    newMediaGallery.dataset.mediaGallerySwapped = 'false';

    this.replaceWith(newMediaGallery);
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * ✅ Dedupe thumbnails by image src/currentSrc (works even without data-media-id)
   */
  #dedupeThumbnails() {
    // Try a few common thumbnail container patterns inside Horizon
    const containers = [
      this.querySelector('[role="tablist"]'),
      this.querySelector('[class*="thumbnail"]'),
      this.querySelector('[class*="thumb"]'),
      this.querySelector('slideshow-component'),
      this.querySelector('slideshow'),
    ].filter(Boolean);

    // If none found, fallback: scan all lists within the gallery
    const fallbackLists = Array.from(this.querySelectorAll('ul, ol, div')).slice(0, 30);
    const targets = containers.length ? containers : fallbackLists;

    targets.forEach((container) => {
      // Collect likely thumbnail items (buttons/links/list items)
      const items = Array.from(container.querySelectorAll('button, a, li, [role="tab"]'));

      const seen = new Set();
      items.forEach((item) => {
        // Prefer an image key
        const img = item.querySelector('img');
        const src =
          img?.currentSrc ||
          img?.src ||
          img?.getAttribute('src') ||
          img?.getAttribute('data-src') ||
          '';

        // Fallback key if no img
        const href = item.getAttribute?.('href') || '';
        const label = item.getAttribute?.('aria-label') || '';
        const key = (src || href || label || item.textContent || '').trim();

        if (!key) return;

        // Also hide obvious clones if present
        const isHiddenClone =
          item.getAttribute?.('aria-hidden') === 'true' ||
          item.className?.toString().includes('clone') ||
          item.closest?.('[aria-hidden="true"]');

        if (isHiddenClone) {
          item.remove();
          return;
        }

        if (seen.has(key)) {
          item.remove();
        } else {
          seen.add(key);
        }
      });
    });
  }

  /**
   * ✅ Watch for Horizon/Slideshow re-rendering thumbnails and dedupe again
   */
  #startThumbObserver() {
    this.#thumbObserver?.disconnect();

    this.#thumbObserver = new MutationObserver(() => {
      this.#dedupeThumbnails();
    });

    this.#thumbObserver.observe(this, {
      childList: true,
      subtree: true,
    });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
